import { StorageProviderForm } from "@humansignal/app-common/blocks/StorageProviderForm";
import { ff } from "@humansignal/core";
import { Button } from "@humansignal/ui";
import { useAtomValue } from "jotai";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Columns } from "../../../components";
import { confirm, modal } from "../../../components/Modal/Modal";
import { Spinner } from "../../../components/Spinner/Spinner";
import { ApiContext } from "../../../providers/ApiProvider";
import { projectAtom } from "../../../providers/ProjectProvider";
import { providers } from "./providers";
import { withSavedConnections } from "./providers/withConnections";
import { StorageCard } from "./StorageCard";
import { StorageForm } from "./StorageForm";

export const StorageSet = forwardRef(
  (
    {
      title,
      target,
      rootClass,
      buttonLabel,
      // Props from parent for lifted state
      storageTypes,
      storages,
      storagesLoaded,
      loading,
      loaded,
      fetchStorages,
    },
    ref,
  ) => {
    const api = useContext(ApiContext);
    const project = useAtomValue(projectAtom);

    const useNewStorageScreen = ff.isActive(ff.FF_NEW_STORAGES);

    // Saved bucket connections of the organisation (credentials stay on
    // the server). Refreshed whenever the storage list changes so a
    // connection added a moment ago is offered for the target storage.
    const [connections, setConnections] = useState([]);
    useEffect(() => {
      if (!project?.id) return;
      api
        .callApi("storageConnections", { params: { project: project.id }, errorFilter: () => true })
        .then((res) => setConnections(res && !res.error ? (res.results ?? []) : []));
    }, [api, project?.id, storages]);
    const providersForForm = useMemo(
      () => withSavedConnections(providers, connections, { target, projectId: project?.id }),
      [connections, target, project?.id],
    );

    const showStorageFormModal = useCallback(
      async (storage) => {
        // Load the saved connections BEFORE the modal opens: the wizard gets
        // its providers once, so a click right after page load must not
        // race the fetch and lose the Connection choice.
        let providersNow = providersForForm;
        if (project?.id) {
          const res = await api
            .callApi("storageConnections", { params: { project: project.id }, errorFilter: () => true })
            .catch(() => null);
          if (res && !res.error) {
            const fresh = res.results ?? [];
            setConnections(fresh);
            providersNow = withSavedConnections(providers, fresh, { target, projectId: project.id });
          }
        }
        const action = storage ? "Edit" : "Connect";
        const actionTarget = target === "export" ? "Target" : "Source";
        const title = `${action} ${actionTarget} Storage`;

        const modalRef = modal({
          title,
          closeOnClickOutside: false,
          style: { width: 840 },
          bare: useNewStorageScreen,
          onHidden: () => {
            // Reset state when modal is closed (including Escape key)
            // This ensures clean state for next modal open
          },
          body: useNewStorageScreen ? (
            <StorageProviderForm
              title={title}
              target={target}
              storage={storage}
              project={project.id}
              rootClass={rootClass}
              storageTypes={storageTypes}
              providers={providersNow}
              onSubmit={async () => {
                modalRef.close();
                fetchStorages();
              }}
              onHide={() => {
                // This will be called when the modal is closed via Escape key
                // The state reset is handled inside StorageProviderForm
              }}
            />
          ) : (
            <StorageForm
              target={target}
              storage={storage}
              project={project.id}
              rootClass={rootClass}
              storageTypes={storageTypes}
              onSubmit={async () => {
                await fetchStorages();
                modalRef.close();
              }}
            />
          ),
        });
      },
      [project, fetchStorages, target, rootClass, providersForForm, storageTypes, useNewStorageScreen, api],
    );

    const onEditStorage = useCallback(
      async (storage) => {
        showStorageFormModal(storage);
      },
      [showStorageFormModal],
    );

    // Expose showStorageFormModal to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        openAddModal: () => showStorageFormModal(),
      }),
      [showStorageFormModal],
    );

    const onDeleteStorage = useCallback(
      async (storage) => {
        confirm({
          title: "Deleting storage",
          body: "This action cannot be undone. Are you sure?",
          buttonLook: "negative",
          onOk: async () => {
            const response = await api.callApi("deleteStorage", {
              params: {
                type: storage.type,
                pk: storage.id,
                target,
              },
            });

            if (response !== null) fetchStorages();
          },
        });
      },
      [fetchStorages],
    );

    return (
      <Columns.Column title={title}>
        <div className={rootClass.elem("controls").toClassName()}>
          <Button
            onClick={() => showStorageFormModal()}
            disabled={loading}
            look="outlined"
            data-testid={`add-${target === "export" ? "target" : "source"}-storage-button`}
            aria-label={`Add ${target === "export" ? "Target" : "Source"} Storage`}
          >
            {buttonLabel}
          </Button>
        </div>

        {loading && !loaded ? (
          <div className={rootClass.elem("empty").toClassName()}>
            <Spinner size={32} />
          </div>
        ) : storagesLoaded && storages.length === 0 ? null : (
          storages?.map?.((storage) => (
            <StorageCard
              key={storage.id}
              storage={storage}
              target={target}
              rootClass={rootClass}
              storageTypes={storageTypes}
              onEditStorage={onEditStorage}
              onDeleteStorage={onDeleteStorage}
            />
          ))
        )}
      </Columns.Column>
    );
  },
);
